import React__default, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useTransition } from 'react-spring';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';
import { useInfiniteScroll } from '../../lib/infiniteScroll.js';
import { setTestId } from '../../lib/testId.js';
import { Badge } from '../badge/Badge.js';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CheckboxInput } from '../inputs/CheckboxInput.js';
import { Box } from '../layout/Box.js';
import { Column } from '../layout/Column.js';
import { Columns } from '../layout/Columns.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { LoadingSpinner, LoadingSpinnerOverlay } from '../loading/LoadingSpinner.js';
import { Text } from '../text/Text.js';

const ListStack = styled(Stack)`
  /* ul overrides */
  padding: 0;
  list-style-type: none;
  width: 100%;
  text-align: left;
  margin-block-start: 0;
`;
const ListLoadMoreSpinner = styled(LoadingSpinner)``;
const ListLoadMoreTracker = styled.li`
  height: 1px;
  width: 100%;
`;
const List = ({ loading =false , overflow ="scroll" , loadingMore =false , onLoadMore =undefined , hasNextPage =true , children , ...props })=>{
    const [header, rows] = useQueryChildComponents(children, [
        ListHeaderRow
    ]);
    const scroll = useInfiniteScroll({
        onLoadMore: onLoadMore ?? (()=>null),
        loading: loadingMore,
        hasNextPage,
        // Arbitrary margin so that items are loaded before 'bottom' of scroll is reached
        containerMargin: "0px 0px 150px 0px"
    });
    // Delay the loading exit, so that the List has enough time to rerender before 'loading' is complete
    const transition = useTransition(loading, {
        from: {
            opacity: 0
        },
        enter: {
            opacity: 1
        },
        leave: {
            opacity: 0
        },
        trail: 200
    });
    return React__default.createElement(Box, {
        height: "fill",
        width: "fill",
        overflow: overflow === "scroll" ? "hidden" : undefined,
        "data-hpy-web-list": true
    }, onLoadMore !== undefined && React__default.createElement(AutoSizer, null, ({ height , width  })=>React__default.createElement(Stack, {
            style: {
                height: `${height}px`,
                width: `${width}px`
            }
        }, header, React__default.createElement(Box, {
            ref: scroll.containerRef,
            overflow: overflow
        }, React__default.createElement(ListStack, {
            ...props,
            as: "ul"
        }, rows, React__default.createElement(ListLoadMoreTracker, {
            ref: scroll.scrollElementRef
        }))), React__default.createElement(Box, {
            width: "fill",
            minHeight: 3
        }, React__default.createElement(Inline, {
            align: "center",
            alignY: "center",
            width: "fill"
        }, loadingMore && React__default.createElement(ListLoadMoreSpinner, {
            size: "small",
            testId: "List-LoadMore"
        }))), transition((_, show)=>show && React__default.createElement(LoadingSpinnerOverlay, {
                size: "medium",
                bgOverlay: "partial",
                testId: "List"
            })))), onLoadMore === undefined && React__default.createElement(Stack, null, header, React__default.createElement(Box, {
        overflow: overflow
    }, React__default.createElement(ListStack, {
        ...props,
        as: "ul"
    }, rows)), transition((_, show)=>show && React__default.createElement(LoadingSpinnerOverlay, {
            size: "medium",
            bgOverlay: "partial",
            testId: "List"
        }))));
};
List.dataId = "data-hpy-web-list";
List.displayName = "List";
const ListRowBox = styled(Box)`
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0 0.5rem;
  overflow: hidden;

  &:hover,
  &:focus-within {
    border-color: ${COLOR_PALETTE.slateGrey10};
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  }

  & > * {
    ${(props)=>props.$selectable && "cursor: pointer;"}
  }
`;
const ListRow = ({ id , selectable =true , forwardedRef , children , ...props })=>{
    const [content, others] = useQueryChildComponents(children, [
        ListData
    ]);
    if (others?.length > 0) {
        throw new HappyWebSystemError("ListRow", "Only ListData elements are valid as children.");
    }
    return React__default.createElement(ListRowBox, {
        ref: forwardedRef,
        "data-hpy-web-list-row": true,
        "$selectable": selectable,
        ...props,
        as: "li"
    }, React__default.createElement(Columns, {
        as: selectable ? "label" : undefined,
        ...setTestId("ListRow-" + id)
    }, content));
};
ListRow.dataId = "data-hpy-web-list-row";
ListRow.displayName = "ListRow";
const ListData = ({ span , align ="left" , weight ="regular" , children , ...props })=>{
    const [checkbox, content] = useQueryChildComponents(children, [
        CheckboxInput
    ]);
    return React__default.createElement(Column, {
        "data-hpy-web-list-data": true,
        ...props,
        span: span
    }, React__default.createElement(Box, {
        padding: [
            0,
            0.5
        ],
        minHeight: 3,
        width: "fill"
    }, React__default.createElement(Inline, {
        alignY: "center",
        align: align,
        wrap: false,
        height: "fill",
        width: "fill",
        gap: 0.5
    }, checkbox, content.map((c, i)=>typeof c === "string" ? React__default.createElement(Text, {
            key: i,
            truncate: true,
            weight: weight,
            size: "medium"
        }, c) : c))));
};
ListData.dataId = "data-hpy-web-list-data";
ListData.displayName = "ListData";
const ListHeaderRowBox = styled(Box)`
  position: relative;
  /* Dividing line */
  &::after {
    content: '';
    width: calc(100% - 2rem);
    height: 1px;
    background: ${COLOR_PALETTE.slateGrey10};
    position: absolute;
    bottom: -1px;
    left: 1rem;
  }
`;
const ListHeaderRow = ({ children , ...props })=>{
    const [content, others] = useQueryChildComponents(children, [
        ListHeader
    ]);
    if (others?.length > 0) {
        throw new HappyWebSystemError("ListHeaderRow", "Only ListHeader elements are valid as children.");
    }
    return React__default.createElement(ListHeaderRowBox, {
        padding: [
            0,
            0.5
        ],
        "data-hpy-web-listrow": true,
        ...props
    }, React__default.createElement(Columns, null, content));
};
ListHeaderRow.dataId = "data-hpy-web-list-header-row";
ListHeaderRow.displayName = "ListRow";
const ListHeaderInline = styled(Inline)`
  min-height: 1.25rem;
`;
const ListHeader = ({ span , align ="left" , children , ...props })=>{
    const [badge, heading] = useQueryChildComponents(children, [
        Badge
    ]);
    return React__default.createElement(Column, {
        "data-hpy-web-list-header": true,
        ...props,
        span: span
    }, React__default.createElement(Box, {
        padding: [
            1,
            0.5
        ],
        width: "fill"
    }, React__default.createElement(ListHeaderInline, {
        alignY: "center",
        align: align,
        wrap: false,
        height: "fill",
        width: "fill",
        gap: 0.5
    }, badge, heading.map((c, i)=>typeof c === "string" ? React__default.createElement(Text, {
            key: i,
            truncate: false,
            size: "small",
            weight: "bold",
            color: "slateGrey80"
        }, c) : c))));
};
ListHeader.dataId = "data-hpy-web-list-header";
ListHeader.displayName = "ListHeader";
const useListSelection = (all, onToggle)=>{
    const allRef = useRef(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    allRef.current = useMemo(()=>new Set(all), [
        all,
        all.length
    ]);
    const [selected, setSelected] = useState([]);
    const selectedRef = useRef(new Set());
    selectedRef.current = useMemo(()=>new Set(selected), [
        selected
    ]);
    useEffect(()=>{
        const newRef = new Set();
        for (const v of all){
            if (selectedRef.current.has(v)) {
                newRef.add(v);
            }
        }
        if (selectedRef.current.size !== newRef.size) {
            selectedRef.current = newRef;
            const arr = Array.from(selectedRef.current);
            setSelected(arr);
            if (onToggle !== undefined) {
                onToggle(arr);
            }
        }
    }, [
        all,
        all.length,
        onToggle
    ]);
    const toggle = useCallback((v)=>{
        if (selectedRef.current.has(v)) {
            selectedRef.current.delete(v);
        } else {
            selectedRef.current.add(v);
        }
        const arr = Array.from(selectedRef.current);
        setSelected(arr);
        if (onToggle !== undefined) {
            onToggle(arr);
        }
    }, [
        onToggle
    ]);
    const toggleAll = useCallback(()=>{
        if (selectedRef.current.size === allRef.current.size) {
            selectedRef.current = new Set();
        } else {
            selectedRef.current = new Set([
                ...allRef.current
            ]);
        }
        const arr = Array.from(selectedRef.current);
        setSelected(arr);
        if (onToggle !== undefined) {
            onToggle(arr);
        }
    }, [
        onToggle
    ]);
    const clear = useCallback(()=>{
        selectedRef.current = new Set();
        setSelected([]);
        if (onToggle !== undefined) {
            onToggle([]);
        }
    }, [
        onToggle
    ]);
    const value = useMemo(()=>{
        return {
            selected,
            setSelected,
            toggle,
            toggleAll,
            clear
        };
    }, [
        clear,
        selected,
        toggle,
        toggleAll
    ]);
    return value;
};

export { List, ListData, ListHeader, ListHeaderRow, ListRow, useListSelection };
